import type { FormEvent, ReactNode } from 'react';

import { Button, ProgressButton } from '@/components/ui';

/**
 * Gemeinsamer Rahmen der Administrationsformulare (`IwmAdminBenutzerForm.razor`,
 * `IwmAdminGruppeForm.razor`, `IwmAdminKategorieForm.razor`, `IwmAdminViewForm.razor`).
 *
 * Seit REQ-25 §4.6 in der Gestalt der ChinaApp: Kopf mit Titel und Werkzeugleiste
 * `New`/`Refresh`/`Delete`, darunter der Inhalt, unten eine haftende Fußleiste mit `Save`.
 * Die Bootstrap-Zeilen des Rahmens sind entfallen; Felder stehen im zweispaltigen Raster
 * `iwm-admin-form-grid`, das die Formulare selbst setzen.
 */
export interface AdminFormFrameProps {
  /** Kopf-Titel, z. B. der Name des Datensatzes; ohne Angabe kein Titel. */
  title?: string;
  /** `Model != null && Model.Id > 0`. */
  showDelete: boolean;
  saving: boolean;
  isValid: boolean;
  /** Zeigt links neben `Save` den Hinweis „Ungespeicherte Änderungen". */
  dirty?: boolean;
  onNew: () => void;
  onRefresh: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  onSave: () => void | Promise<void>;
  children: ReactNode;
}

export function AdminFormFrame(props: AdminFormFrameProps) {
  // `EditForm` fängt das Absenden ab; der Speichern-Knopf ist trotzdem `type="submit"`.
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <div className="iwm-form iwm-admin-form">
      <form onSubmit={onSubmit}>
        <div className="iwm-admin-form-head">
          {props.title === undefined || props.title === '' ? null : (
            <h3 className="iwm-admin-form-title">{props.title}</h3>
          )}
          <div className="iwm-admin-form-toolbar">
            <Button content="New" iconClass="e-icons e-plus" onClick={props.onNew} />
            <Button content="Refresh" iconClass="e-icons e-refresh" onClick={props.onRefresh} />
            {props.showDelete ? (
              <Button content="Delete" iconClass="e-icons e-delete" onClick={props.onDelete} />
            ) : null}
          </div>
        </div>
        <div className="iwm-admin-form-content">{props.children}</div>
        <div className="iwm-admin-form-footer">
          {props.dirty === true ? (
            <span className="iwm-admin-form-dirty">Ungespeicherte Änderungen</span>
          ) : null}
          <ProgressButton
            content="Save"
            primary
            type="submit"
            progress={props.saving}
            disabled={!props.isValid}
            onClick={props.onSave}
          />
        </div>
      </form>
    </div>
  );
}

export default AdminFormFrame;
